import { useEffect, useRef } from 'react';

/**
 * useHeading — Fires compass/GPS heading via DOM events for the directional arrow.
 * No React state updates. No map rotation.
 */
export default function useHeading() {
  const lastEmitRef = useRef(0);
  const lastValueRef = useRef(0);
  const speedRef = useRef(0);

  useEffect(() => {
    const emitHeading = (h) => {
      const now = Date.now();
      const diff = Math.abs(((h - lastValueRef.current + 540) % 360) - 180);
      if (diff >= 5 && now - lastEmitRef.current > 500) {
        lastValueRef.current = h;
        lastEmitRef.current = now;
        window.dispatchEvent(new CustomEvent('crisis-heading', { detail: { heading: h, speed: speedRef.current } }));
      }
    };

    const compassHandle = (event) => {
      if (event.alpha == null) return;
      const h = Math.round((360 - event.alpha) % 360);
      if (speedRef.current < 2) emitHeading(h);
    };

    let watchId;
    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const { heading, speed } = pos.coords;
          if (speed != null) speedRef.current = speed;
          if (heading != null && !isNaN(heading) && speed > 2) {
            emitHeading(Math.round(heading));
          }
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 3000 }
      );
    } catch {}

    if (typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function') {
      DeviceOrientationEvent.requestPermission()
        .then(state => {
          if (state === 'granted') {
            window.addEventListener('deviceorientationabsolute', compassHandle, true);
            window.addEventListener('deviceorientation', compassHandle, true);
          }
        })
        .catch(() => {});
    } else {
      window.addEventListener('deviceorientationabsolute', compassHandle, true);
      window.addEventListener('deviceorientation', compassHandle, true);
    }

    return () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      window.removeEventListener('deviceorientationabsolute', compassHandle, true);
      window.removeEventListener('deviceorientation', compassHandle, true);
    };
  }, []);

  return { heading: 0, needsCalibration: false };
}
